/**
 * ย่อ + บีบรูปก่อนแปลงเป็น base64 DataURL
 *
 * สลิปโอนเงินที่ถ่าย/แคปจากมือถือมักใหญ่ 3-8MB และ base64 ยังพองอีก ~33%
 * ตอนยิงขึ้น API ฟังก์ชันนี้ย่อด้านยาวสุดลงแล้ว re-encode ใหม่จนต่ำกว่าเพดาน
 * ปลายทางเป็นคนเปิดดูสลิปเอง (ไม่มี OCR) ค่า default จึงเน้นเล็กเป็นหลัก
 *
 * ผลลัพธ์ต้องเป็น jpeg หรือ png เท่านั้น — ปลายทาง (/mainwallet/uploadSlip และ
 * /p2c/upload-slip) ไม่รับ webp ห้าม re-encode เป็น webp แม้เบราว์เซอร์จะทำได้
 * และไฟล์จะเล็กกว่าก็ตาม
 *
 * แต่ "ปลายทางไม่รับ webp" คือเหตุผลที่ต้อง *แปลงให้* ไม่ใช่เหตุผลที่จะปฏิเสธไฟล์
 * ตั้งแต่ตอนเลือก — สลิปบนมือถือเป็น .webp บ่อยมาก (LINE, แกลเลอรี Android) ถ้าบล็อก
 * ทิ้งลูกค้ากลุ่มนั้นจะฝากเงินไม่ได้เลย ทุกชนิดที่เบราว์เซอร์ decode ได้จึงถูกรับเข้ามา
 * แล้ว re-encode เป็น jpeg ให้เสมอ เหลือปฏิเสธเฉพาะไฟล์ที่ decode ไม่ออกจริง ๆ
 *
 * ถ้าเบราว์เซอร์ decode ไฟล์ไม่ได้ (เช่น HEIC บน Android) จะ fallback ไปอ่าน
 * ไฟล์ดิบแทน — อัปโหลดได้เหมือนเดิม แค่ไม่ถูกบีบ ไม่ทำให้ผู้ใช้ฝากไม่ได้
 */

export type CompressImageOptions = {
	/** ความยาวด้านที่ยาวที่สุดหลังย่อ (px) — ไม่ขยายรูปที่เล็กกว่านี้ */
	maxEdge?: number;
	/** ขนาด DataURL สูงสุดที่ยอมรับ (bytes) */
	maxBytes?: number;
	/** คุณภาพเริ่มต้น 0-1 */
	quality?: number;
	/** คุณภาพต่ำสุดที่ยอมไล่ลดลงไปถึง */
	minQuality?: number;
};

const DEFAULT_OPTIONS: Required<CompressImageOptions> = {
	maxEdge: 1280,
	maxBytes: 300 * 1024,
	quality: 0.85,
	minQuality: 0.5,
};

/** ชนิดไฟล์ที่ปลายทางรับได้ — ส่งดิบขึ้นไปตรง ๆ ได้โดยไม่ต้องแปลง */
export const ACCEPTED_IMAGE_TYPES: string[] = ['image/jpeg', 'image/png'];

const isAcceptedType = (type: string): boolean => ACCEPTED_IMAGE_TYPES.includes(type);

const readFileAsDataURL = (file: File): Promise<string> =>
	new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = () => resolve(reader.result as string);
		reader.onerror = () => reject(reader.error ?? new Error('อ่านไฟล์ไม่สำเร็จ'));
		reader.readAsDataURL(file);
	});

// ถึงตรงนี้แปลว่า decode ไม่ผ่าน — jpeg/png ยังส่งดิบขึ้นไปได้เพราะปลายทางรับอยู่แล้ว
// ที่เหลือ (เช่น HEIC บนเบราว์เซอร์ที่เปิดไม่ได้) ไปต่อไม่ได้จริง ๆ ต้องบอกให้ผู้ใช้
// เปลี่ยนไฟล์ ไม่ใช่ปล่อยชนิดที่ปลายทางไม่รับหลุดขึ้นไปแล้วไปตายที่ API
const passThroughOrReject = (file: File): Promise<string> =>
	isAcceptedType(file.type)
		? readFileAsDataURL(file)
		: Promise.reject(new Error('ไฟล์นี้เปิดไม่ได้ กรุณาใช้ไฟล์ JPG หรือ PNG'));

export const compressImageToDataURL = async (
	file: File,
	options: CompressImageOptions = {},
): Promise<string> => {
	const { maxEdge, maxBytes, quality, minQuality } = { ...DEFAULT_OPTIONS, ...options };

	// เล็กอยู่แล้วและชนิดถูกอยู่แล้ว ไม่ต้องแตะ — re-encode ไปก็ไม่ได้อะไรกลับมา
	if (file.size <= maxBytes && isAcceptedType(file.type)) return readFileAsDataURL(file);

	let bitmap: ImageBitmap;
	try {
		// imageOrientation: 'from-image' — ไม่งั้นรูปจากกล้อง iPhone จะหมุนผิดด้านตาม EXIF
		bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
	} catch {
		return passThroughOrReject(file);
	}

	try {
		const scale = Math.min(1, maxEdge / Math.max(bitmap.width, bitmap.height));
		const canvas = document.createElement('canvas');
		canvas.width = Math.max(1, Math.round(bitmap.width * scale));
		canvas.height = Math.max(1, Math.round(bitmap.height * scale));

		const ctx = canvas.getContext('2d');
		if (!ctx) return passThroughOrReject(file);

		// ถมขาวก่อนวาด — PNG พื้นโปร่งใสจะกลายเป็นดำสนิทเมื่อ encode เป็น jpeg
		ctx.fillStyle = '#ffffff';
		ctx.fillRect(0, 0, canvas.width, canvas.height);
		ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);

		// jpeg เท่านั้น — เป็นชนิดเดียวที่ทั้งปลายทางรับและ canvas ปรับ quality ได้
		// (png ไม่รับ quality และไฟล์ใหญ่กว่าหลายเท่า ใช้บีบสลิปไม่ได้)
		let q = quality;
		let dataUrl = canvas.toDataURL('image/jpeg', q);
		while (dataUrl.length > maxBytes && q > minQuality) {
			q = Math.max(minQuality, q - 0.1);
			dataUrl = canvas.toDataURL('image/jpeg', q);
		}
		return dataUrl;
	} finally {
		bitmap.close();
	}
};
